import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import type { WebViewerInstance } from '@pdftron/webviewer';
import { FileModel } from '../../../../models';
import { Logger } from '../../../../services/logger.service';
import { WebViewerPreloadService } from '../../../../services/webviewer/webviewer-preload.service';
import { SnackbarService, SnackbarType } from '../../../snackbar';
import { FilePreviewControlsComponent } from '../../file-preview-controls/file-preview-controls.component';
import { PreviewSource } from '../../processors/file-preview-processor';
import { FilePreviewTelemetryService } from '../../telemetry/file-preview-telemetry.service';
import type { RenderedMeta } from '../../telemetry/file-preview-telemetry.types';
import { getFileExtension, supportsTextSearch } from '../../utils/file-extension.util';
import {
  ACTIVE_SEARCH_HIGHLIGHT_COLOR,
  ApryseSearchResult,
  MAX_ZOOM_PCT,
  MIN_ZOOM_PCT,
  NATIVE_CHROME_ELEMENTS,
  SEARCH_HIGHLIGHT_COLOR,
  ZOOM_STEP,
} from './apryse-renderer.utils';

@Component({
  selector: 'app-apryse-renderer',
  templateUrl: './apryse-renderer.component.html',
  styleUrls: ['./apryse-renderer.component.scss'],
  imports: [FilePreviewControlsComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApryseRendererComponent implements OnDestroy {
  readonly file = input.required<Partial<FileModel>>();
  readonly source = input.required<PreviewSource>();
  /** VIEW_ONLY secure links: locks the WebViewer's own print/download hotkeys and buttons, not just our chrome. */
  readonly canDownload = input<boolean>(true);

  readonly documentLoaded = output<RenderedMeta>();
  readonly loadError = output<{ stage: 'render' | 'instance'; error: unknown }>();
  /** 0..1 download fraction from the WebViewer downloader — URL-streamed loads only. */
  readonly loadingProgress = output<number>();

  /** True once the current document has rendered — gates the floating controls overlay. */
  readonly loaded = signal(false);

  /** True while a print job is in flight (rendering → browser dialog open). Read-only to consumers. */
  readonly printing = signal(false);

  // Viewer state, kept in sync with the instance via Core events. Read-only to consumers.
  readonly zoomPercent = signal(100);
  readonly currentPage = signal(1);
  readonly pageCount = signal(0);
  readonly canZoomIn = computed(() => this.zoomPercent() < MAX_ZOOM_PCT);
  readonly canZoomOut = computed(() => this.zoomPercent() > MIN_ZOOM_PCT);

  readonly searchTotal = signal(0);
  /** 1-based index of the active match; 0 when there is no active match. */
  readonly searchCurrent = signal(0);

  /**
   * Whether the loaded file type carries a searchable text layer. Re-evaluates per file, so
   * search availability stays correct as the user steps through a multi-file preview.
   */
  protected readonly canSearch = computed(() => supportsTextSearch(this.file()?.name));

  private readonly _viewerRef = viewChild.required<ElementRef<HTMLDivElement>>('viewer');

  private readonly _preloadService = inject(WebViewerPreloadService);
  private readonly _logger = inject(Logger);
  private readonly _snackbar = inject(SnackbarService);
  // Dialog-scoped: resolved through the element injector from the parent FilePreviewComponent.
  private readonly _telemetry = inject(FilePreviewTelemetryService);

  private _destroyed = false;
  private _instance: WebViewerInstance | null = null;
  private _instanceReady: Promise<WebViewerInstance | null> | null = null;
  /** Min zoom the shared instance had before we clamped it — restored on release for the signing flows. */
  private _priorMinZoom: number | null = null;
  private _documentLoadedHandler: (() => void) | null = null;
  private _loadErrorHandler: ((event: unknown) => void) | null = null;
  private _zoomUpdatedHandler: ((zoom: number) => void) | null = null;
  private _pageNumberUpdatedHandler: ((page: number) => void) | null = null;
  /** One-shot `afterprint` listener; cleared once the browser print dialog closes. */
  private _afterPrintHandler: (() => void) | null = null;
  /** Whether a print attempt is mid-flight — gates the `printed` telemetry so teardown of a
   *  never-started job (endPrint via ngOnDestroy) does not emit a bogus completion. */
  private _printInFlight = false;

  /** Accumulated search hits for the current term, backing next/prev navigation and counts. */
  private _searchResults: ApryseSearchResult[] = [];

  constructor() {
    // React to input changes — load (or reload) the document each time `source`/`file` changes.
    effect(() => {
      const file = this.file();
      const source = this.source();
      if (!file || !source) return;
      void this.loadInto(file, source);
    });
  }

  /**
   * Print via the browser's native dialog, skipping Apryse's own print-options modal.
   * `printInBackground` renders every page (Office/images included) then hands off to
   * `window.print()`, so the user picks paper/orientation/range in the browser dialog.
   */
  print(): void {
    const inst = this._instance;
    if (!inst || this.printing()) return; // guard re-entry while a job is in flight

    this._telemetry.mark('print');
    this._printInFlight = true;
    this.printing.set(true);
    // The browser fires `afterprint` when the native dialog closes (printed or cancelled).
    this._afterPrintHandler = () => this.endPrint();
    window.addEventListener('afterprint', this._afterPrintHandler, { once: true });

    try {
      inst.UI.printInBackground({ includeAnnotations: true });
    } catch (err) {
      // Synchronous failure never reaches `window.print()`, so `afterprint` won't fire — reset now.
      this._logger.error('ApryseRendererComponent :: print failed', err);
      this._snackbar.open('We couldn’t print this file. Please try again.', SnackbarType.ERROR);
      this._telemetry.printed('failed', { pageCount: this.pageCount() });
      this._printInFlight = false;
      this.endPrint();
    }
  }

  /** Tear down the print state + listener. Safe to call multiple times. */
  private endPrint(): void {
    if (this._afterPrintHandler) {
      window.removeEventListener('afterprint', this._afterPrintHandler);
      this._afterPrintHandler = null;
    }
    // Only report completion for an actual in-flight attempt — not teardown of a never-started job
    // (ngOnDestroy also calls endPrint). The synchronous-throw path reports 'failed' and clears the
    // flag before reaching here, so this stays the 'completed' (afterprint) path only.
    if (this._printInFlight) {
      this._printInFlight = false;
      this._telemetry.printed('completed', { pageCount: this.pageCount() });
    }
    this.printing.set(false);
  }

  // ── Zoom ────────────────────────────────────────────────────────────────────────
  zoomIn(): void {
    this.applyZoom(this.zoomPercent() * ZOOM_STEP);
  }

  zoomOut(): void {
    this.applyZoom(this.zoomPercent() / ZOOM_STEP);
  }

  setZoomPercent(pct: number): void {
    this.applyZoom(pct);
  }

  /** Reset to fit-width (also drops back toward 100%). */
  fitWidth(): void {
    const inst = this._instance;
    if (!inst) return;
    inst.UI.setFitMode(inst.UI.FitMode.FitWidth);
  }

  private applyZoom(pct: number): void {
    const dv = this._instance?.Core.documentViewer;
    if (!dv) return;

    const clamped = Math.min(Math.max(Math.round(pct), MIN_ZOOM_PCT), MAX_ZOOM_PCT);
    dv.zoomTo(clamped / 100);
    // `zoomPercent` is updated reactively by the zoomUpdated listener, never optimistically here,
    // so fit-mode and ctrl-scroll zoom can't desync the displayed value.
  }

  // ── Pages ───────────────────────────────────────────────────────────────────────
  goToPage(page: number): void {
    const dv = this._instance?.Core.documentViewer;
    if (!dv) return;

    const max = Math.max(this.pageCount(), 1);
    const clamped = Math.min(Math.max(Math.round(page), 1), max);
    dv.setCurrentPage(clamped, true);
  }

  nextPage(): void {
    this.goToPage(this.currentPage() + 1);
  }

  prevPage(): void {
    this.goToPage(this.currentPage() - 1);
  }

  // ── Search ──────────────────────────────────────────────────────────────────────
  /** Performance needs to be evaluated on large documents. */
  search(term: string): void {
    const inst = this._instance;
    if (!inst) return;
    const { documentViewer, Search } = inst.Core;

    // (Re)start: drop any previous results/highlights.
    documentViewer.clearSearchResults();
    this._searchResults = [];
    this.searchTotal.set(0);
    this.searchCurrent.set(0);

    if (!term?.trim()) return;

    const modes = [Search.Mode.PAGE_STOP, Search.Mode.HIGHLIGHT, Search.Mode.AMBIENT_STRING];
    documentViewer.textSearchInit(term, modes, {
      fullSearch: true,
      onResult: (result: ApryseSearchResult) => {
        if (result?.resultCode === Search.ResultCode.FOUND) {
          this._searchResults.push(result);
          documentViewer.displayAdditionalSearchResult(result);
          this.searchTotal.set(this._searchResults.length);
        }
      },
      onDocumentEnd: () => {
        this.searchTotal.set(this._searchResults.length);
        if (this._searchResults.length > 0) this.goToMatch(0);
        // Engagement signal — length + counts only, NEVER the raw term (PII §5 / D1).
        this._telemetry.searched({
          termLength: term.trim().length,
          resultCount: this._searchResults.length,
          zeroResults: this._searchResults.length === 0,
        });
      },
    });
  }

  nextMatch(): void {
    if (this._searchResults.length === 0) return;
    // searchCurrent is 1-based, so it already points at the next zero-based index.
    this.goToMatch(this.searchCurrent());
  }

  prevMatch(): void {
    if (this._searchResults.length === 0) return;
    this.goToMatch(this.searchCurrent() - 2);
  }

  clearSearch(): void {
    this._instance?.Core.documentViewer.clearSearchResults();
    this._searchResults = [];
    this.searchTotal.set(0);
    this.searchCurrent.set(0);
  }

  /** Activates the match at `index` (wrapping around the ends) and updates the 1-based counter. */
  private goToMatch(index: number): void {
    const dv = this._instance?.Core.documentViewer;
    const n = this._searchResults.length;
    if (!dv || n === 0) return;
    const wrapped = ((index % n) + n) % n;
    dv.setActiveSearchResult(this._searchResults[wrapped]);
    this.searchCurrent.set(wrapped + 1);
  }

  private async loadInto(file: Partial<FileModel>, source: PreviewSource): Promise<void> {
    // Hide the controls while the next document loads; the documentLoaded handler re-shows them.
    this.loaded.set(false);

    const instance = await this.ensureInstance();
    if (this._destroyed) return;
    if (!instance) {
      this.loadError.emit({ stage: 'instance', error: new Error('WebViewer instance unavailable') });
      return;
    }

    const extension = getFileExtension(file.name);

    this.attachDocumentEvents(instance);

    try {
      // Close any previously loaded document before loading the next one to keep memory tidy.
      try {
        await instance.Core.documentViewer.closeDocument();
      } catch {
        // No-op: closeDocument throws if there is no active doc, which is fine on first load.
      }
      this._telemetry.mark('render');
      if (source.kind === 'url') {
        instance.UI.loadDocument(source.url, {
          extension,
          filename: file.name,
          onLoadingProgress: (fraction: number) => this.loadingProgress.emit(fraction),
        });
      } else {
        instance.UI.loadDocument(source.blob, { extension, filename: file.name });
      }
    } catch (err) {
      this._logger.error('ApryseRendererComponent :: loadDocument failed', err);
      this.loadError.emit({ stage: 'render', error: err });
    }
  }

  private async ensureInstance(): Promise<WebViewerInstance | null> {
    if (this._instance) return this._instance;
    if (!this._instanceReady) {
      this._instanceReady = this.acquireAndMount();
    }
    return this._instanceReady;
  }

  private async acquireAndMount(): Promise<WebViewerInstance | null> {
    this._telemetry.mark('acquire');
    // acquire() resolves to null when nothing was ever preloaded — kick preload off first (idempotent).
    this._preloadService.preload();
    const preloaded = await this._preloadService.acquire();
    if (!preloaded) {
      this._logger.error('ApryseRendererComponent :: No WebViewer instance available');
      return null;
    }
    if (this._destroyed) {
      // Destroyed mid-acquire: hand the instance back so it stays reusable, and skip
      // confirmAcquisition() — otherwise the singleton is orphaned and every later preview breaks.
      this._preloadService.release(preloaded.instance, preloaded.container);
      preloaded.container.remove();
      return null;
    }

    const viewerEl = this._viewerRef().nativeElement;
    while (preloaded.container.firstChild) {
      viewerEl.appendChild(preloaded.container.firstChild);
    }
    preloaded.container.remove();
    this._preloadService.confirmAcquisition();

    // The recycled instance is shared with the signing flows — remember what we change so
    // ngOnDestroy can hand it back in the state they expect.
    this._priorMinZoom = preloaded.instance.UI.getMinZoomLevel();
    preloaded.instance.UI.enableViewOnlyMode();
    preloaded.instance.UI.setMinZoomLevel(1);

    // Belt-and-suspenders: re-assert the native chrome is hidden on the (recycled) instance, and
    // disable the fading floating page-navigation arrows, which the `disabledElements` config does
    // not cover. Both are replaced by our own FilePreviewControlsComponent.
    preloaded.instance.UI.disableElements(NATIVE_CHROME_ELEMENTS);
    preloaded.instance.UI.disableFadePageNavigationComponent();

    // VIEW_ONLY secure links: our own header hides the download/print buttons, but the WebViewer
    // instance still answers to Ctrl/Cmd+P and its own print/download UI unless told otherwise.
    if (!this.canDownload()) {
      const { Feature } = preloaded.instance.UI;
      preloaded.instance.UI.disableFeatures([Feature.Print, Feature.Download]);
    }
    // Brand the search highlights (primary palette) instead of Apryse's default yellow.
    preloaded.instance.Core.documentViewer.setSearchHighlightColors({
      searchResult: SEARCH_HIGHLIGHT_COLOR,
      activeSearchResult: ACTIVE_SEARCH_HIGHLIGHT_COLOR,
    });

    this._instance = preloaded.instance;
    return preloaded.instance;
  }

  private attachDocumentEvents(instance: WebViewerInstance): void {
    const dv = instance.Core.documentViewer;
    this.detachDocumentEvents(instance);

    this._documentLoadedHandler = () => {
      instance.UI.setFitMode(instance.UI.FitMode.FitWidth);
      this.resetViewerState(instance);
      this.loaded.set(true);
      this.documentLoaded.emit({ pageCount: this.pageCount() });
    };
    // Document load failures surface through the UI `loaderror` event, NOT a documentViewer
    // event — WebViewer has no `documentLoadFailed`. The payload arrives as a CustomEvent whose
    // `detail` holds the underlying error.
    this._loadErrorHandler = (event: unknown) => {
      const err = event instanceof CustomEvent ? event.detail : event;
      this.loaded.set(false);
      this._logger.error('ApryseRendererComponent :: document load error', err);
      this.loadError.emit({ stage: 'render', error: err });
    };
    this._zoomUpdatedHandler = (zoom: number) => this.zoomPercent.set(Math.round(zoom * 100));
    this._pageNumberUpdatedHandler = (page: number) => this.currentPage.set(page);

    dv.addEventListener('documentLoaded', this._documentLoadedHandler);
    dv.addEventListener('zoomUpdated', this._zoomUpdatedHandler);
    dv.addEventListener('pageNumberUpdated', this._pageNumberUpdatedHandler);
    instance.UI.addEventListener(instance.UI.Events.LOAD_ERROR, this._loadErrorHandler);
  }

  private detachDocumentEvents(instance: WebViewerInstance): void {
    const dv = instance.Core.documentViewer;
    if (this._documentLoadedHandler) dv.removeEventListener('documentLoaded', this._documentLoadedHandler);
    if (this._zoomUpdatedHandler) dv.removeEventListener('zoomUpdated', this._zoomUpdatedHandler);
    if (this._pageNumberUpdatedHandler) dv.removeEventListener('pageNumberUpdated', this._pageNumberUpdatedHandler);
    if (this._loadErrorHandler) instance.UI.removeEventListener(instance.UI.Events.LOAD_ERROR, this._loadErrorHandler);
  }

  /** Reset all viewer state for a freshly loaded document (the instance is recycled across files). */
  private resetViewerState(instance: WebViewerInstance): void {
    const dv = instance.Core.documentViewer;
    this.clearSearch();
    this.pageCount.set(dv.getPageCount());
    this.currentPage.set(dv.getCurrentPage());
    this.zoomPercent.set(Math.round(dv.getZoomLevel() * 100));
  }

  ngOnDestroy(): void {
    this._destroyed = true;

    // Drop any pending `afterprint` listener so a dialog open at teardown can't touch a destroyed signal.
    this.endPrint();

    if (!this._instance) return;

    const dv = this._instance.Core.documentViewer;
    this.detachDocumentEvents(this._instance);

    try {
      dv.closeDocument();
    } catch {
      // ignore
    }

    this.restoreSharedInstanceState(this._instance);
    this._preloadService.release(this._instance, this._viewerRef().nativeElement);
    this._instance = null;
    this._instanceReady = null;
  }

  /**
   * The recycled WebViewer singleton is shared with the signing flows (view-and-sign,
   * engagement steps) — undo the view-only lock, hidden chrome, and zoom clamp we applied
   * on acquire so the next consumer gets the instance in the state it was preloaded with.
   */
  private restoreSharedInstanceState(instance: WebViewerInstance): void {
    try {
      instance.UI.disableViewOnlyMode();
      instance.UI.enableElements(NATIVE_CHROME_ELEMENTS);
      instance.UI.enableFeatures([instance.UI.Feature.Print, instance.UI.Feature.Download]);
      if (this._priorMinZoom !== null) {
        instance.UI.setMinZoomLevel(this._priorMinZoom);
      }
    } catch (err) {
      this._logger.warn('ApryseRendererComponent :: failed to restore shared WebViewer state on release', err);
    }
  }
}
